import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import { 
  Plus, 
  Search, 
  Package, 
  Edit, 
  Trash2,
  Filter,
  Image,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

// Filters now use dynamic categories and storage locations from the API.

export default function Products() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "all");
  const [locationFilter, setLocationFilter] = useState(searchParams.get("location") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [deleteId, setDeleteId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchFilters();
  }, [categoryFilter, locationFilter, statusFilter]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      if (locationFilter && locationFilter !== "all") params.append("storage_location", locationFilter);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [catsRes, locsRes] = await Promise.all([
        api.get("/categories"),
        api.get("/storage-locations"),
      ]);
      setCategories(catsRes.data || []);
      setLocations(locsRes.data || []);
    } catch (error) {
      toast.error("Failed to load filter options");
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Product deleted");
      setProducts(products.filter(p => p.product_id !== deleteId));
    } catch (error) {
      toast.error("Failed to delete product");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryLabel = (value) => value || "";
  const getLocationLabel = (value) => value || "";

  return (
    <div data-testid="products-page" className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground tracking-tight">{t("products.title")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t("products.subtitle")}</p>
        </div>
        <Link to="/products/add">
          <Button size="sm" data-testid="add-product-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t("products.addProduct")}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("products.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="category-filter">
                  <Filter className="h-3.5 w-3.5 mr-1.5" />
                  <SelectValue placeholder={optionsLoading ? "Loading..." : "Category"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("products.allCategories")}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="location-filter">
                  <SelectValue placeholder={optionsLoading ? "Loading..." : "Location"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("products.allLocations")}</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.name}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[120px] h-9 text-xs" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("products.allStatus")}</SelectItem>
                  <SelectItem value="valid">{t("products.valid")}</SelectItem>
                  <SelectItem value="warning">{t("products.warning")}</SelectItem>
                  <SelectItem value="expired">{t("products.expired")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Package className="h-8 w-8 mb-3 opacity-40" />
              <p className="text-sm">{t("products.noProducts")}</p>
              <Link to="/products/add" className="mt-3">
                <Button variant="outline" size="sm" className="text-xs" data-testid="empty-add-product-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  {t("products.addFirst")}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50/80">
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">{t("products.product")}</TableHead>
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">{t("products.category")}</TableHead>
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">{t("products.location")}</TableHead>
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">{t("products.expiration")}</TableHead>
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">{t("products.status")}</TableHead>
                    <TableHead className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground text-right">{t("products.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.product_id} className="hover:bg-zinc-50 transition-colors">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-md bg-zinc-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-zinc-200">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover" />
                            ) : (
                              <Package className="h-5 w-5 text-zinc-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{product.name}</p>
                            {product.batch_number && (
                              <p className="text-[10px] text-muted-foreground font-mono">#{product.batch_number}</p>
                            )}
                            {product.supplier && (
                              <p className="text-[10px] text-muted-foreground truncate">{product.supplier}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{getCategoryLabel(product.category)}</TableCell>
                      <TableCell className="text-sm">{getLocationLabel(product.storage_location)}</TableCell>
                      <TableCell className="font-mono text-xs">{product.expiration_date}</TableCell>
                      <TableCell>
                        <StatusBadge status={product.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/products/${product.product_id}/edit`}>
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-${product.product_id}`}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7" 
                            onClick={() => setDeleteId(product.product_id)}
                            data-testid={`delete-${product.product_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("products.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("products.deleteDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
